import React, { useState, useEffect } from 'react';
import {
  Badge,
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Typography,
  Box,
  Button,
  Divider,
  Chip,
  Avatar,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsNone as NotificationsNoneIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
  MarkEmailRead as MarkEmailReadIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { getApiBase } from '../utils/apiConfig';

const NotificationCenter = ({ token }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const headers = token && token !== 'cookie' ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${getApiBase()}/notifications/me`, { credentials: 'include', headers })
      .then(response => response.ok ? response.json() : { notifications: [], unreadCount: 0 })
      .then(data => {
        setNotifications(Array.isArray(data.notifications) ? data.notifications : []);
        setUnreadCount(Number(data.unreadCount) || 0);
      })
      .catch(() => {
        setNotifications([]);
        setUnreadCount(0);
      });
  }, [token]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = (notification) => {
    if (!notification.readAt) {
      markAsRead(notification._id);
    }
    if (notification.actionUrl) window.location.href = notification.actionUrl;
    handleClose();
  };

  const markAsRead = (notificationId) => {
    const headers = token && token !== 'cookie' ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${getApiBase()}/notifications/${notificationId}/read`, { method: 'PUT', credentials: 'include', headers });
    setNotifications(prev =>
      prev.map(n =>
        n._id === notificationId ? { ...n, readAt: new Date().toISOString() } : n
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllAsRead = () => {
    const headers = token && token !== 'cookie' ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${getApiBase()}/notifications/read-all/me`, { method: 'PUT', credentials: 'include', headers });
    setNotifications(prev => prev.map(n => ({ ...n, readAt: n.readAt || new Date().toISOString() })));
    setUnreadCount(0);
  };

  const deleteNotification = (notificationId, event) => {
    event.stopPropagation();
    const headers = token && token !== 'cookie' ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${getApiBase()}/notifications/${notificationId}`, { method: 'DELETE', credentials: 'include', headers });
    setNotifications(prev => prev.filter(n => n._id !== notificationId));
    const notification = notifications.find(n => n._id === notificationId);
    if (notification && !notification.readAt) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  };

  const getNotificationIcon = (type, priority) => {
    const iconProps = { 
      sx: { 
        color: priority === 'high' ? 'error.main' : 
               priority === 'medium' ? 'warning.main' : 'info.main' 
      } 
    };

    switch (type) {
      case 'booking':
        return <CheckCircleIcon {...iconProps} />;
      case 'payment':
        return <InfoIcon {...iconProps} />;
      case 'review':
        return <CheckCircleIcon {...iconProps} />;
      case 'system':
        return <WarningIcon {...iconProps} />;
      default:
        return <InfoIcon {...iconProps} />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton
        onClick={handleClick}
        size="large"
        aria-label={`${unreadCount} unread notifications`}
        color="inherit"
        sx={{
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: 'action.hover',
            transform: 'scale(1.05)',
          },
        }}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          variant={unreadCount > 0 ? 'standard' : 'dot'}
          invisible={unreadCount === 0}
        >
          {unreadCount > 0 ? <NotificationsIcon /> : <NotificationsNoneIcon />}
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            width: isMobile ? '90vw' : 400,
            maxHeight: 500,
            overflow: 'hidden',
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          },
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Notifications
            </Typography>
            {unreadCount > 0 && (
              <Button
                size="small"
                onClick={markAllAsRead}
                startIcon={<MarkEmailReadIcon />}
                sx={{ minWidth: 'auto' }}
              >
                Mark all read
              </Button>
            )}
          </Box>
          {unreadCount > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {unreadCount} unread notification{unreadCount > 1 ? 's' : ''}
            </Typography>
          )}
        </Box>

        {notifications.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <NotificationsNoneIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No notifications yet
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0, maxHeight: 350, overflow: 'auto' }}>
            {notifications.map((notification, index) => (
              <React.Fragment key={notification._id}>
                <ListItem
                  button
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    alignItems: 'flex-start',
                    py: 1.5,
                    backgroundColor: notification.readAt ? 'inherit' : 'action.hover',
                    '&:hover': {
                      backgroundColor: notification.readAt ? 'action.hover' : 'action.selected',
                    },
                    borderLeft: notification.readAt ? 'none' : `3px solid ${theme.palette[getPriorityColor(notification.priority)].main}`,
                  }}
                >
                  <ListItemIcon sx={{ mt: 0.5, minWidth: 40 }}>
                    {getNotificationIcon(notification.type, notification.priority)}
                  </ListItemIcon>
                  
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: notification.readAt ? 400 : 600,
                            flex: 1,
                          }}
                        >
                          {notification.title}
                        </Typography>
                        <Chip
                          label={notification.priority}
                          size="small"
                          color={getPriorityColor(notification.priority)}
                          variant="outlined"
                          sx={{ fontSize: '0.65rem', height: 20 }}
                        />
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 0.5 }}
                        >
                          {notification.message}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.disabled"
                          sx={{ fontSize: '0.7rem' }}
                        >
                          {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                        </Typography>
                      </>
                    }
                  />
                  
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={(e) => deleteNotification(notification._id, e)}
                      sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
                
                {index < notifications.length - 1 && <Divider variant="inset" />}
              </React.Fragment>
            ))}
          </List>
        )}

        {notifications.length > 0 && (
          <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider', textAlign: 'center' }}>
            <Button
              size="small"
              href="/mover-portal/notifications"
              sx={{ textTransform: 'none' }}
            >
              View All Notifications
            </Button>
          </Box>
        )}
      </Popover>
    </>
  );
};

export default NotificationCenter;